import { download, generateCsv, mkConfig } from "export-to-csv"
import { GDPRUserDeletionPeriod } from "../gdprTypes"
import { useDateTime } from "../../../../../localization/useDateTime"
import { DateTime } from "luxon"
import { isEmpty } from "lodash"
import { GDPRUsersByDomainResponse } from "../gdprApi"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"

const createEmptyRow = (startColumn: number, totalColumns: number): Record<string, string> =>
    Array.from({ length: totalColumns - startColumn + 1 }).reduce(
        (acc: Record<string, string>, _, index) => {
            acc[`column${startColumn + index}`] = ""
            return acc
        },
        {} as Record<string, string>,
    )

export const useExportUsersData = () => {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()

    const csvOptions = mkConfig({
        fieldSeparator: ";",
        quoteStrings: true,
        decimalSeparator: ".",
        useBom: true,
        useKeysAsHeaders: true,
        showColumnHeaders: false,
        filename: `Facilitynet-export-users-${dateTimeFactory.formatDate(DateTime.now())}`,
    })

    const handleExportRows = (
        data: GDPRUsersByDomainResponse,
        userType: string[],
        userDeletionPeriod?: GDPRUserDeletionPeriod,
    ) => {
        if (!data.users) return

        const metadataRows = [
            createEmptyRow(1, 14),
            { column1: "User types:", column2: userType.join(", "), ...createEmptyRow(3, 14) },
            {
                column1: "Verified domains:",
                column2: data.reportDetails?.verifiedDomains?.join(", ") || "",
                ...createEmptyRow(3, 14),
            },
            {
                column1: "User deletion period:",
                column2: userDeletionPeriod
                    ? l10n.getStringForEnum(
                          "supplier-account-gdpr-export-user-deletion-period-select-item",
                          userDeletionPeriod,
                          "",
                      )
                    : "-",
                ...createEmptyRow(3, 14),
            },
            createEmptyRow(1, 14),
        ]

        const headerRow = {
            column1: "Initials",
            column2: "Given Name",
            column3: "Surname",
            column4: "Email",
            column5: "Locale Phone",
            column6: "Mobile Number",
            column7: "Username",
            column8: "User Group",
            column9: "Company",
            column10: "User Type",
            column11: "Roles",
            column12: "Created",
            column13: "Updated",
            column14: "Deleted",
        }

        const userRows = data.users.map((user) => ({
            column1: user.initials,
            column2: user.givenName,
            column3: user.surname,
            column4: user.email,
            column5: user.localePhone,
            column6: user.mobileNumber,
            column7: user.username,
            column8: user.userGroup.name,
            column9: user.company.name,
            column10: user.userType,
            column11: user?.roles?.join(", "),
            column12: !isEmpty(user.history.created)
                ? dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(user.history.created.timestamp))
                : "",
            column13: !isEmpty(user.history.updated)
                ? dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(user.history.updated.timestamp))
                : "",
            column14: !isEmpty(user.history.deleted)
                ? dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(user.history.deleted.timestamp))
                : "",
        }))

        const exportRows = [...metadataRows, headerRow, ...userRows]

        const csv = generateCsv(csvOptions)(exportRows)
        download(csvOptions)(csv)
    }

    return { handleExportRows }
}
